"use client";

import { t } from "i18next";
import { ChevronLeft, Info, Loader2, RotateCcw } from "lucide-react";
import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { AppConfig } from "@/common/appConfig";
import { ALLOWED_AUDIO_MIME_TYPES } from "@/common/config";
import { StatusFile, ValidationSuscriptionButton } from "@/common/enums/enums";
import {
  useWorkspaceInline,
  WorkspacePanel,
} from "@/components/layout/v3/WorkspacePanel";
import { Button } from "@/components/ui/button";
import { MicrophoneButton } from "@/components/ui/MicrophoneButton";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from "@/components/ui/sheet";
import { AdvancedSettings } from "@/components/ui/visits/AdvancedSettings";
import { useToast } from "@/hooks/use-toast";
import { useAudioValidation } from "@/hooks/useAudioValidation";
import { useUnloadWarning } from "@/hooks/useUnloadWarning";
import { useAudioRecordingSubmit } from "@/hooks/visits/useAudioRecordingSubmit";
import { useRecordingSubmit } from "@/hooks/visits/useRecordingSubmit";
import { useVisitReads } from "@/hooks/visits/VisitReads";
import {
  addRecordingBlock,
  addRecordingBlockMessage,
  noteChangedSinceWritten,
  replacedNoteVersion,
} from "@/lib/add-recording";
import { getBlobFromUrl } from "@/lib/getBlobFromUrl";
import getDuration from "@/lib/getDuration";
import { cn } from "@/lib/utils";
import { markVisitProcessing } from "@/lib/visit-queries";
import { useAuthStore } from "@/store/auth.store";
import {
  recordingConsentField,
  useRecordingConsentStore,
} from "@/store/recordingConsent.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { AlertMessage } from "../AlertMessage";
import AudioRecorderUI from "../audio-recorder";
import RecorderAudioPlayer from "../audio-recorder/recorder-audio-player";
import ConfirmDialog from "../ConfirmDialog";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { SwitchTranscriptionRealtime } from "../SwitchTranscriptionRealtime";
import { ToastAction } from "../toast";
import { V3Button, V3IconButton } from "../v3/button";
import { V3ConfirmSheet } from "../v3/confirm-sheet";
import { NewVisitSetupV32 } from "./NewVisitSetupV32";
import { PreChartTemplateChip } from "./PreChartTemplateChip";
import { RecorderNoteWriting } from "./RecorderNoteWriting";
import { UploadAudioButton } from "./UploadRecordButton";
import { VisitSubmissionNotice } from "./VisitSubmissionNotice";

export function MultiRecordingSidebar({
  open,
  onOpenChange,
  mode = "add",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // "startVisit": Start visit on a pre-chart draft. The same recorder, titled
  // Start visit, recording as soon as it opens; after Stop the note is
  // written from the recording with the pre-chart.
  mode?: "add" | "startVisit";
}): JSX.Element {
  const startVisit = mode === "startVisit";
  const panelTitle = startVisit
    ? t("preChart.startVisit")
    : t("visit.workspace.addRecording");
  const submittedMessage = startVisit
    ? t("preChart.recorder.submitted")
    : t("visit.sidebar.multiRecording.messages.uploadSuccessful.description");
  const { toast } = useToast();
  const [showRecorder, setShowRecorder] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [audioFinished, setAudioFinished] = useState(false);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState<string | null>(null);
  // The chosen file (its name and size head the v3.2 recording card).
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [preparingSubmission, setPreparingSubmission] = useState(false);
  const [transcription, setTranscription] = useState<string | null>(null);
  // v3.2 recorder: Stop writes the note at once (as New visit does); a
  // failed submission waits for Try again.
  const [autoSubmit, setAutoSubmit] = useState<"idle" | "running" | "failed">(
    "idle",
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  // Recorded audio lives only in this tab until it is submitted.
  useUnloadWarning(
    open && showRecorder && !isFileUpload,
    t(
      "visits.sidebar.unsavedRecording",
      "Your recording has not been submitted. Leaving this page will lose it.",
    ),
  );
  const { file, multiRecordingFiles, refresh, setVersionId } = useVisitReads();
  // Add recording writes the main note again from all the recordings: a
  // note changed after it was written (edits, corrected values) loses those
  // changes, and the doctor is told before recording. The note it replaces
  // is kept in Version history (walkthrough 31/10, M-A).
  const noteChanged = !startVisit && noteChangedSinceWritten(file);
  // Versions there when a changed note's recording was submitted; once the
  // rebuilt note is ready, a toast links to the version that holds the note
  // it replaced.
  const pendingRebuild = useRef<string[] | null>(null);
  const rememberRebuild = (): void => {
    pendingRebuild.current =
      noteChanged && file
        ? file.versions.map((version) => String(version.public_id))
        : null;
  };
  useEffect(() => {
    const before = pendingRebuild.current;
    if (!before || !file) return;
    if (file.status === StatusFile.ERROR) {
      pendingRebuild.current = null;
      return;
    }
    if (file.status !== StatusFile.READY) return;
    const replaced = replacedNoteVersion(before, file.versions);
    if (!replaced) return;
    pendingRebuild.current = null;
    toast({
      title: t("visit.sidebar.multiRecording.rebuilt"),
      action: (
        <ToastAction
          altText={t("visit.sidebar.multiRecording.viewPrevious")}
          onClick={() => setVersionId(replaced)}
        >
          {t("visit.sidebar.multiRecording.viewPrevious")}
        </ToastAction>
      ),
    });
  }, [file, toast, setVersionId]);
  const changedNotice = (className: string): JSX.Element | null =>
    noteChanged ? (
      <p
        className={cn(
          "flex gap-2 rounded-xl bg-v3-warn-tint px-3 py-2.5 text-v3-warn-text",
          className,
        )}
        data-testid="add-recording-note-changed"
        role="note"
      >
        <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
        <span>{t("visit.sidebar.multiRecording.noteChanged")}</span>
      </p>
    ) : null;
  // Re-checked here because the sidebar can stay open while the visit starts
  // processing (e.g. a recording added from another tab).
  const block = addRecordingBlock(file, multiRecordingFiles);
  const blocked = addRecordingBlockMessage(block, t);
  const userData = useAuthStore((state) => state.userData);
  const uid = useAuthStore((state) => state.user?.uid);
  const identity = `${uid}:${userData?.public_id}:${file?.public_id}`;
  const lifecycle = useRef({ identity, open, generation: 0 });
  if (
    lifecycle.current.identity !== identity ||
    lifecycle.current.open !== open
  ) {
    lifecycle.current = {
      identity,
      open,
      generation: lifecycle.current.generation + 1,
    };
  }
  const renderGeneration = lifecycle.current.generation;
  useEffect(
    () => () => {
      lifecycle.current.generation++;
    },
    [],
  );
  const alreadyCompletedRef = useRef<boolean>(false);
  const { error, validateFile, resetError } = useAudioValidation();
  // Text-only submissions: a live transcript whose audio is empty (nothing
  // to keep), and recovery of a pending text-only submission.
  const {
    submitRecording,
    beginDraft,
    abandon,
    recovery,
    loading: submitRecordingLoading,
    error: submitRecordingError,
  } = useRecordingSubmit({ visitPublicId: file?.public_id });
  const audio = useAudioRecordingSubmit({
    visitPublicId: file?.public_id,
    open,
  });
  const { inputLanguage, saveIsRealtimeEnabled } = useRecordingSettingsStore();

  const isCreatingRecording =
    preparingSubmission || submitRecordingLoading || audio.loading;

  const handlePreventCloseOnInteraction = (event: Event): void => {
    if (showRecorder || isFileUpload || submitting) {
      event.preventDefault();
    }
  };

  const handleRecordingComplete = (
    blobUrl: string,
    transcription: string,
  ): void => {
    if (
      alreadyCompletedRef.current ||
      lifecycle.current.generation !== renderGeneration ||
      !lifecycle.current.open
    )
      return;
    alreadyCompletedRef.current = true;

    setRecordedBlobUrl(blobUrl);
    setAudioFinished(true);
    setTranscription(transcription);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const fileUploaded = event.target.files?.[0];
    if (fileUploaded && validateFile(fileUploaded)) {
      const fileUrl = URL.createObjectURL(fileUploaded);
      setSelectedFile(fileUploaded);
      setRecordedBlobUrl(fileUrl);
      setAudioFinished(true);
      setIsFileUpload(true);
    }
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!recordedBlobUrl || !userData?.public_id || !file?.public_id || blocked)
      return false;
    const generation = lifecycle.current.generation;
    const current = (): boolean =>
      lifecycle.current.generation === generation && lifecycle.current.open;
    if (!current()) return false;
    setPreparingSubmission(true);
    setSubmitting(true);
    try {
      if (transcription) {
        const blob = await getBlobFromUrl(recordedBlobUrl);
        const duration = await getDuration(blob);
        if (!current()) return false;
        const body = {
          duration,
          language: inputLanguage,
          transcription,
          // Start visit: the pre-chart's first recording carries consent.
          ...(startVisit ? recordingConsentField() : {}),
        };
        // The live transcript is kept as is, with the recorded audio
        // (uploaded first) so the recording can be played back, as a new
        // visit keeps its audio. Without audio, the transcript alone.
        const accepted =
          blob.size > 0
            ? await audio.submit({ ...body, file: blob })
            : await submitRecording(body);
        if (accepted && current()) {
          rememberRebuild();
          if (startVisit) markVisitProcessing(file.public_id);
          toast({
            description: submittedMessage,
          });
          onOpenChange(false);
          // The visit is now processing (a pre-chart is no longer a draft).
          void refresh();
          return true;
        }
        return false;
      } else {
        const blob = await getBlobFromUrl(recordedBlobUrl);
        const duration = await getDuration(blob);
        if (!current()) return false;
        if (
          await audio.submit({ file: blob, duration, language: inputLanguage })
        ) {
          if (!current()) return false;
          rememberRebuild();
          if (startVisit) markVisitProcessing(file.public_id);
          toast({
            description: submittedMessage,
          });
          onOpenChange(false);
          // The visit is now processing (a pre-chart is no longer a draft).
          void refresh();
          return true;
        }
        return false;
      }
    } catch {
      if (!current()) return false;
      setSubmitting(false);
      toast({
        variant: "error",
        title: t("visit.sidebar.multiRecording.messages.requestFailed.title"),
        description: t(
          "visit.sidebar.multiRecording.messages.requestFailed.description",
        ),
      });
    } finally {
      if (current()) {
        setSubmitting(false);
        setPreparingSubmission(false);
      }
    }
    return false;
  };

  const resetStates = useCallback((): void => {
    lifecycle.current.generation++;
    setShowRecorder(false);
    setIsFileUpload(false);
    setAudioFinished(false);
    setRecordedBlobUrl(null);
    setSelectedFile(null);
    setTranscription(null);
    setPreparingSubmission(false);
    setSubmitting(false);
    setAutoSubmit("idle");
    useRecordingConsentStore.getState().reset();
    alreadyCompletedRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  useEffect(() => {
    if (!open) {
      resetStates();
      resetError();
    }
  }, [open, resetError, resetStates]);

  const previousIdentity = useRef(identity);
  useEffect(() => {
    if (previousIdentity.current !== identity) {
      previousIdentity.current = identity;
      resetStates();
    }
  }, [identity, resetStates]);

  // v3.2 app: before recording, the v3.2 New visit screen (its own back
  // button, notice and actions; no footer).
  const v3Setup = AppConfig.enableAppRedesign && !showRecorder && !isFileUpload;
  // v3.2 app with the v3.2 recorder (VITE_RECORDER_V32_ENABLED): the same
  // recorder as New visit (top bar, live transcript, waveform pill, Stop
  // sheet), then the note is written at once.
  const v32Recording =
    AppConfig.enableAppRedesign &&
    AppConfig.enableRecorderV32 &&
    showRecorder &&
    !isFileUpload;
  const v32Writing = v32Recording && audioFinished && autoSubmit !== "failed";
  const v32Failed = v32Recording && audioFinished && autoSubmit === "failed";
  const v32Screen = v3Setup || v32Recording;
  // v3.2 app, a chosen file before it is sent: the v3.2 recording card and
  // one primary action, Back to cancel.
  const v3Upload = AppConfig.enableAppRedesign === true && isFileUpload;
  const [confirmDiscardV32, setConfirmDiscardV32] = useState(false);
  const discardRecording = (): void => {
    abandon();
    audio.abandon();
    resetStates();
    onOpenChange(false);
  };
  const submitNow = async (): Promise<void> => {
    setAutoSubmit("running");
    if (!(await handleSubmit())) setAutoSubmit("failed");
  };
  const submitNowRef = useRef(submitNow);
  submitNowRef.current = submitNow;
  // Stop (v3.2): submit once the recording is complete.
  useEffect(() => {
    if (
      v32Recording &&
      audioFinished &&
      recordedBlobUrl &&
      autoSubmit === "idle"
    )
      void submitNowRef.current();
  }, [v32Recording, audioFinished, recordedBlobUrl, autoSubmit]);

  const startRecorder = (): void => {
    if (!blocked && !audio.recovery && beginDraft() && audio.beginDraft())
      setShowRecorder(true);
  };
  // Start visit opens the New visit setup first (clinical lead, 29/10):
  // language, microphone and consent are checked before recording, with
  // the pre-chart's template shown; changing it rewrites the draft under the
  // new template (PreChartTemplateChip, clinician feedback 03/11).

  // Live transcription is this sidebar's default. Apply it when the sidebar
  // opens, not when it mounts: it is mounted on every visit page, and setting
  // it there changed the toggle (and the diarize default) for new visits too.
  useEffect(() => {
    if (open) saveIsRealtimeEnabled(true);
  }, [open, saveIsRealtimeEnabled]);

  // v3.2 desktop: open in the visit's column (no sheet, no scrim).
  const inline = useWorkspaceInline();
  const content = (
    <>
      <input
        ref={fileInputRef}
        accept={ALLOWED_AUDIO_MIME_TYPES.join(",")}
        style={{ display: "none" }}
        type="file"
        onChange={handleFileChange}
      />
      <div className="flex flex-col flex-1 h-full">
        {v3Upload && (
          <header className="flex h-14 shrink-0 items-center gap-1 px-1.5 font-v3">
            <V3IconButton
              disabled={isCreatingRecording || submitting}
              label={t("recorderV32.back")}
              onClick={() => setShowCancelConfirmation(true)}
            >
              <ChevronLeft className="!size-5" />
            </V3IconButton>
            <p className="text-[15px] font-semibold text-v3-text-2">
              {t("newVisitV32.upload")}
            </p>
          </header>
        )}
        <div
          className={
            v32Screen
              ? "flex min-h-0 flex-1 flex-col"
              : v3Upload
                ? "flex min-h-0 flex-1 flex-col gap-5 px-5 pb-6 pt-2 font-v3 text-v3-text"
                : "flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10"
          }
          data-testid={v3Upload ? "add-recording-upload-v3" : undefined}
        >
          {v3Upload && (
            <h2 className="font-v3-display text-[30px] font-bold leading-9 tracking-[-0.02em]">
              {panelTitle}
            </h2>
          )}
          {isFileUpload ? (
            recordedBlobUrl && (
              <RecorderAudioPlayer
                file={selectedFile ?? undefined}
                mediaBlobUrl={recordedBlobUrl}
              />
            )
          ) : showRecorder && v32Recording ? (
            <>
              {/* Stays mounted while the note is written, so a failed
                  submission keeps this recording. */}
              <div className={v32Writing || v32Failed ? "hidden" : "contents"}>
                <AudioRecorderUI
                  added={{
                    title: panelTitle,
                    subtitle: file?.title?.trim() || undefined,
                    consent: startVisit,
                  }}
                  noteSettings={
                    <>
                      <p
                        className="flex gap-2 text-[13px] leading-[18px] text-v3-text-3"
                        data-testid="add-recording-notice"
                      >
                        <Info aria-hidden className="mt-px size-4 shrink-0" />
                        <span>
                          {startVisit
                            ? t("preChart.recorder.notice")
                            : t("visit.sidebar.multiRecording.notice")}
                        </span>
                      </p>
                      {changedNotice("text-[13px] leading-[18px]")}
                    </>
                  }
                  onDiscard={discardRecording}
                  onFinished={setAudioFinished}
                  onRecordingComplete={handleRecordingComplete}
                />
              </div>
              {v32Writing && (
                <div className="flex flex-1 items-center justify-center">
                  <RecorderNoteWriting
                    hint={
                      startVisit
                        ? t("preChart.recorder.writingHint")
                        : t("visit.sidebar.multiRecording.writingHint")
                    }
                    templateName=""
                    title={
                      startVisit
                        ? t("preChart.recorder.writingTitle")
                        : t("visit.sidebar.multiRecording.writingTitle")
                    }
                  />
                </div>
              )}
              {v32Failed && (
                <div
                  className="flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center font-v3"
                  data-testid="add-recording-failed"
                  role="alert"
                >
                  <p className="text-[17px] font-semibold text-v3-text">
                    {t(
                      "visit.sidebar.multiRecording.messages.requestFailed.title",
                    )}
                  </p>
                  <p className="max-w-[320px] text-[14px] leading-5 text-v3-text-3">
                    {t(
                      "visit.sidebar.multiRecording.messages.requestFailed.description",
                    )}
                  </p>
                  <div className="mt-2 flex flex-wrap justify-center gap-2">
                    <V3Button
                      disabled={isCreatingRecording || submitting}
                      variant="primary"
                      onClick={() => {
                        void submitNow();
                      }}
                    >
                      <RotateCcw aria-hidden />
                      {t("preChart.recorder.retry")}
                    </V3Button>
                    <V3Button
                      variant="ghost"
                      onClick={() => setConfirmDiscardV32(true)}
                    >
                      {t("recorderV32.discard")}
                    </V3Button>
                  </div>
                </div>
              )}
            </>
          ) : showRecorder ? (
            <AudioRecorderUI
              onFinished={setAudioFinished}
              onRecordingComplete={handleRecordingComplete}
            />
          ) : AppConfig.enableAppRedesign ? (
            // v3.2 app: the New visit screen in "add to this visit" mode
            // (no template or consent: the visit has them).
            <NewVisitSetupV32
              uploadWithLive
              consent={startVisit && AppConfig.enableRecordingConsent}
              disabled={!!blocked}
              templateChip={
                startVisit && file ? (
                  <PreChartTemplateChip file={file} />
                ) : undefined
              }
              title={panelTitle}
              onClose={() => onOpenChange(false)}
              onStart={startRecorder}
              onUpload={() => {
                if (!blocked && beginDraft() && audio.beginDraft())
                  fileInputRef.current?.click();
              }}
            >
              {blocked ? (
                <p
                  className="rounded-xl bg-v3-warn-tint px-4 py-3 text-[14px] leading-5 text-v3-warn-text"
                  data-testid="add-recording-blocked"
                  role="alert"
                >
                  {blocked}
                </p>
              ) : (
                <>
                  <p
                    className="flex gap-2 text-[14px] leading-5 text-v3-text-3"
                    data-testid="add-recording-notice"
                  >
                    <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
                    <span>
                      {startVisit
                        ? t("preChart.recorder.notice")
                        : t("visit.sidebar.multiRecording.notice")}
                    </span>
                  </p>
                  {changedNotice("text-[14px] leading-5")}
                </>
              )}
              {error && (
                <p
                  className="rounded-xl bg-v3-rec-tint px-4 py-3 text-[14px] leading-5 text-v3-rec-text"
                  role="alert"
                >
                  <span className="block font-semibold">
                    {t("error.formRecorder.titleMessage")}
                  </span>
                  {error}
                </p>
              )}
            </NewVisitSetupV32>
          ) : (
            <>
              <MicrophoneButton disabled={!!blocked} onClick={startRecorder} />
              <div className="flex flex-col w-full gap-4">
                <AdvancedSettings />
                <Button
                  className="w-full"
                  disabled={!!blocked}
                  size="xl"
                  onClick={startRecorder}
                >
                  {t("visits.sidebar.recordAudio")}
                </Button>
                <SwitchTranscriptionRealtime />
              </div>
              <Separator />
              <UploadAudioButton
                disabled={!!blocked}
                onClick={() => {
                  if (!blocked && beginDraft() && audio.beginDraft())
                    fileInputRef.current?.click();
                }}
              />
              {error && (
                <AlertMessage
                  description={error}
                  title={t("error.formRecorder.titleMessage")}
                  variant="destructive"
                />
              )}
            </>
          )}
        </div>
        <V3ConfirmSheet
          destructive
          cancelLabel={t("recorderV32.discardConfirm.cancel")}
          confirmLabel={t("recorderV32.discardConfirm.confirm")}
          description={t("recorderV32.discardConfirm.description")}
          open={confirmDiscardV32}
          title={t("recorderV32.discardConfirm.title")}
          onConfirm={discardRecording}
          onOpenChange={setConfirmDiscardV32}
        />
        {/* v3.2: not while the recording is being sent (its pending state is
            not a stuck submission); shown again if sending fails. */}
        {!v32Writing && (
          <VisitSubmissionNotice
            recording
            disabled={isCreatingRecording || submitting}
            error={submitRecordingError}
            recovery={recovery}
            onAbandon={() => {
              abandon();
              resetStates();
            }}
          />
        )}
        {!v32Writing && (
          <VisitSubmissionNotice
            recording
            disabled={isCreatingRecording || submitting}
            error={audio.error}
            recovery={audio.recovery}
            onAbandon={() => {
              if (audio.abandon()) resetStates();
            }}
          />
        )}
        <SheetFooter
          className={cn(
            "bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2",
            v32Screen && "hidden",
            v3Upload &&
              "rounded-none bg-transparent px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 font-v3 sm:px-5",
          )}
        >
          {v32Screen ? null : blocked ? (
            <div role="alert" data-testid="add-recording-blocked">
              <AlertMessage
                description={blocked}
                title={t("visit.workspace.addRecording")}
                variant={block === "busy" ? "info" : "warning"}
              />
            </div>
          ) : (
            <>
              <p
                className={cn(
                  "flex gap-2 pb-2 text-sm text-muted-foreground",
                  v3Upload && "text-[13.5px] leading-5 text-v3-text-3",
                )}
                data-testid="add-recording-notice"
              >
                <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
                <span>
                  {startVisit
                    ? t("preChart.recorder.notice")
                    : t("visit.sidebar.multiRecording.notice")}
                </span>
              </p>
              {changedNotice("text-[13.5px] leading-5")}
            </>
          )}
          {audio.recovery && audio.canRetry && (
            <Button
              disabled={isCreatingRecording || submitting || !!blocked}
              onClick={async () => {
                const generation = lifecycle.current.generation;
                const accepted = await audio.submit();
                if (
                  accepted &&
                  lifecycle.current.open &&
                  lifecycle.current.generation === generation
                )
                  onOpenChange(false);
              }}
            >
              {t(
                "visits.submission.retryRecording",
                "Retry recording submission",
              )}
            </Button>
          )}
          {audioFinished && (
            <SubscriptionOnlyButton
              className={cn("w-full", v3Upload && "h-12 text-[15px]")}
              disabled={isCreatingRecording || submitting}
              disabledReason={blocked}
              size={"xl"}
              validations={
                // Both the live transcript and the audio-only paths send
                // through the audio submission; older text-only drafts may
                // still be pending.
                recovery || audio.recovery
                  ? []
                  : [
                      ValidationSuscriptionButton.PLAN_FREE_TRIAL,
                      ValidationSuscriptionButton.RECORDING_USAGES,
                    ]
              }
              onClick={handleSubmit}
            >
              {v3Upload && (isCreatingRecording || submitting) && (
                <Loader2 aria-hidden className="animate-spin" />
              )}
              {v3Upload
                ? isCreatingRecording || submitting
                  ? t("visits.sidebar.processing")
                  : t("visit.sidebar.multiRecording.addToVisit")
                : isCreatingRecording || submitting
                  ? `${t("visit.sidebar.multiRecording.processing")}...`
                  : t("visit.sidebar.multiRecording.submit")}
            </SubscriptionOnlyButton>
          )}
          <ConfirmDialog
            description={t("visits.sidebar.cancelConfirmation.description")}
            open={showCancelConfirmation}
            title={t("visits.sidebar.cancelConfirmation.title")}
            trigger={
              <Button
                className={cn("w-full", v3Upload && "hidden")}
                disabled={isCreatingRecording || submitting}
                size={"xl"}
                variant={"grey"}
                onClick={() => {
                  setShowCancelConfirmation(true);
                }}
              >
                {t("visits.sidebar.cancel")}
              </Button>
            }
            onOpenChange={setShowCancelConfirmation}
            onConfirm={() => {
              if (!isCreatingRecording && !submitting) {
                resetStates();
                onOpenChange(false);
              }
            }}
          />
        </SheetFooter>
      </div>
    </>
  );
  if (inline)
    return (
      <WorkspacePanel
        label={panelTitle}
        open={open}
        testId="add-recording-workspace"
        onEscape={() => {
          if (isCreatingRecording || submitting) return;
          if (showRecorder || isFileUpload) setShowCancelConfirmation(true);
          else onOpenChange(false);
        }}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {content}
        </div>
      </WorkspacePanel>
    );

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isCreatingRecording && !submitting) {
          onOpenChange(isOpen);
        }
      }}
    >
      <SheetContent
        className={cn(
          "w-full sm:max-w-md p-0 sm:rounded-l-2xl overflow-y-auto",
          (v32Screen || v3Upload) &&
            "bg-v3-panel outline-none [&>button.absolute]:hidden",
        )}
        onEscapeKeyDown={handlePreventCloseOnInteraction}
        onInteractOutside={handlePreventCloseOnInteraction}
      >
        <SheetHeader />
        {content}
      </SheetContent>
    </Sheet>
  );
}
